import React, { useState, ChangeEvent, FC, useEffect, useRef } from 'react';
import PropagateLoader from 'react-spinners/PropagateLoader';
import { extractObjectName } from '../../../utilities/functions';
import { translate } from 'pdf-lib';
import { useTranslate } from '../../../hooks/LanguageProvider';
import TooltipText from '../../TooltipText';

interface Alarm {
  searchByPoi?: string;
  searchFieldPoi?: string;
  searchValuePoi?: string;
  subclass?: string;
  totalPoints?: number;
  ids?: number[];
}

interface PoiAlarmProps {
  alarm: any;
  onChange: (newMetrics: Alarm) => void;
}

interface PoiResult {
  idgeof: number;
  codegeof?: string;
  namegeof: string;
  descriptiongeof?: string;
  attributesgeof?: string;
  areageof: string;
  datecreategeof: string;
  user_id: number;
  intrash: number;
  etatgeof?: string;
  numPoints?: number;
  classgeof?: string;
  typegeof?: string;
}

const PoiAlarm: FC<PoiAlarmProps> = ({ alarm, onChange }) => {
  const [searchByPoi, setSearchByPoi] = useState('0');
  const [searchFieldPoi, setSearchFieldPoi] = useState('');
  const [searchValuePoi, setSearchValuePoi] = useState('');
  const [subclass, setSubclass] = useState('');
  const [totalPoints, setTotalPoints] = useState(0);
  const [poiResults, setPoiResults] = useState<PoiResult[]>([]);
  const [pois, setPois] = useState<PoiResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDiv, setShowDiv] = useState(false);
  const id_user = localStorage.getItem("userid");
  const divRef = useRef<HTMLDivElement>(null);
  const { translate } = useTranslate();
  const [selectedPois, setSelectedPois] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);

  const handleCheckboxChange = (poi: PoiResult) => {
    if (selectedPois.includes(poi.idgeof.toString())) {
      // Si déjà sélectionné, retirer du tableau pois
      removePoiFromList(poi.idgeof.toString());
      setSelectedPois((prev) => prev.filter((id) => id !== poi.idgeof.toString()));
    } else {
      // Si non sélectionné, ajouter au tableau pois
      addPoiToList(poi);
      setSelectedPois((prev) => [...prev, poi.idgeof.toString()]);
    }
  };

  const isChecked = (poiId: string) => selectedPois.includes(poiId);


  //"
  const handleSelectAllChange = () => {
    setSelectAll((prev) => !prev);
    if (!selectAll) {
      const allPois = pois.filter(poi => !selectedPois.includes(poi.idgeof.toString()));  // POIs non sélectionnés
      allPois.forEach(poi => addPoiToList(poi));  // Ajout de tous les POIs non sélectionnés
    } else {
      selectedPois.forEach(poiId => removePoiFromList(poiId));  // Retirer les POIs sélectionnés
    }
  };


  // Vérifie si tous les POIs sont sélectionnés
  useEffect(() => {
    setSelectAll(selectedPois.length === pois.length);
  }, [selectedPois, pois]);

  useEffect(() => {
    if (alarm) {
      setSearchByPoi(alarm.searchByPoi || '0');
      setSearchFieldPoi(alarm.searchFieldPoi || '');
      setSearchValuePoi(alarm.searchValuePoi || '');
      setSubclass(alarm.subclass || '');
      setTotalPoints(alarm.totalPoints || 0);
    }
  }, [alarm]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (divRef.current && !divRef.current.contains(event.target as Node)) {
        setShowDiv(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadSearchPois = (event: ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    setSearchByPoi(value);
    onChange({ ...alarm, searchByPoi: value });
    // Hide the div when changing search criteria
    if (!searchFieldPoi.trim()) {
      setShowDiv(false);
    }
  };

  const handleSearchFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchFieldPoi(value);
    onChange({ ...alarm, searchFieldPoi: value });
    setSearchValuePoi(value);
    searchPois(value);
  };

  const searchPois = async (searchValue: string) => {
    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/search/${searchByPoi}/${searchValue}/${id_user}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const POIS = await response.json();
      const result: PoiResult[] = POIS.data;
      setPoiResults(result);
      setTotalPoints(result.length);

      // Show or hide the div based on POI results and search field
      if (searchValue.trim() === '') {
        setShowDiv(false);
      } else {
        setShowDiv(result.length > 0 || loading);
      }
    } catch (error) {
      console.error('Error fetching POIs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPois = async () => {
    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/poi/finds`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_pois: alarm.ids,
          id_user: id_user,
        }),
      });
  
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
  
      const result: PoiResult[] = await response.json();
      setPois(result);
  
    
      const ids = result.map((poi) => String(poi.idgeof));  
      setSelectedPois(ids);  
      
      
      ;  // Met à jour les cases cochées
    } catch (error) {
      console.error('Error fetching POIs:', error);
    } finally {
      setLoading(false);
    }
  };
  

  useEffect(() => {
    getPois();
  }, [alarm.ids, id_user]);

  const addPoiToList = (poi: PoiResult) => {
    // Check if the POI is already in the list
    if (!pois.find(p => p.idgeof === poi.idgeof)) {
      const updatedPois = [...pois, poi];
      setPois(updatedPois);

      // Update the metrics with the new list of POIs
      onChange({
        ...alarm,
        ids: updatedPois.map(p => p.idgeof),
        totalPoints: updatedPois.length
      });
    }
  };

  const PoiToList = (poi: PoiResult) => {
    if (!pois.some(p => p.idgeof === poi.idgeof)) {
      setPois((prev) => [...prev, poi]);
    }
  };

    // Fonction pour retirer un POI de la liste
    const removePoiFromList = (poiId: string) => {
      const updatedPois = pois.filter(p => p.idgeof.toString() !== poiId);  // Retire le POI de la liste
      setPois(updatedPois);  // Met à jour la liste des POIs
  
      // Mise à jour des métriques avec la nouvelle liste des POIs
      onChange({
        ...alarm,
        ids: updatedPois.map(p => p.idgeof),
        totalPoints: updatedPois.length
      });
    };

  const handlePoiClick = (poi: PoiResult) => {
    addPoiToList(poi);

    setShowDiv(false);
  };

  return (
    <div className="col-md-12" id="extra_cell" style={{ marginTop: '10px' }}>
      <div className="row" id="alarm_4_2">
        <div className="col-md-3 col-sm-3" style={{ marginTop: '2px' }}>
          <div className="input-group">
            <span className="input-group-addon">{translate("Search by")}</span>
            <select
              className="form-control input-sm"
              name="search_by_poi"
              value={searchByPoi}
              onChange={loadSearchPois}
            >
              <option value="36">{translate("Nom")}</option>
              <option value="37">{translate("Code")}</option>
            </select>
          </div>
        </div>
        <div className="col-md-3 col-sm-3" id="search_box_poi" style={{ marginTop: '2px' }}>
          <div className="input-group">
            <span className="input-group-addon">
              <span className="glyphicon glyphicon-search"></span>
            </span>
            <input
              className="form-control input-sm ui-autocomplete-input"
              type="text"
              id="search_field_poi"
              name="search_field_poi"
              placeholder={translate("Search") + "..."}
              value={searchFieldPoi}
              onChange={handleSearchFieldChange}
              autoComplete="off"
            />
            <input type="hidden" name="search_value_poi" value={searchValuePoi} disabled />
          </div>
        </div>
        <div className="col-md-2 col-sm-2" style={{ marginTop: '2px' }}>
          <span>{translate("Total points")}: </span>
          <span id="total_points">{totalPoints}</span>
        </div>
        <div className="col-md-1 col-sm-1" style={{ marginTop: '2px' }}>
          <a
            className="btn btn-default btn-sm pull-right gp"
            href="/poi/add"
            title="Ajouter"
            data-toggle="tooltip"
            target="_blank"
          >
            <i className="fas fa-map-pin"></i>
            {translate("Create POI")}
          </a>
        </div>
      </div>
      {showDiv && (
        <div className="row">
          {loading ? (
            <div className="col-md-12" style={{ marginTop: '5px' }}>
              <div className="overlay" style={{ display: 'none' }}></div>
              <div
                id="alarm_4"
                className='autocomplete-items'
                style={{ border: '1px solid #ccc', overflow: 'auto', backgroundColor: 'white', height: '250px', paddingLeft: '5px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
              >
                <p>
                  <PropagateLoader color={"#123abc"} loading={loading} size={20} />
                </p>
              </div>
            </div>
          ) : poiResults.length > 0 ? (
            <div className="col-md-12" style={{ marginTop: '5px' }}>
              <div className="overlay" style={{ display: 'none' }}></div>
              <div
                id="alarm_4"
                className='autocomplete-items'
                style={{ border: '1px solid #ccc', overflow: 'auto', backgroundColor: 'white', height: '250px', paddingLeft: '5px' }}
              >
                <table id="poi_table" style={{ width: '90%' }}>
                  <thead>
                    <tr>
                      <th>ID POI</th>
                      <th>Nom</th>
                      <th>Code</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {poiResults.map((poi) => (
                      <tr key={poi.idgeof} style={{ cursor: "pointer" }} onClick={() => handlePoiClick(poi)}>
                        <td>{poi.idgeof}</td>
                        <td><TooltipText fullText={poi.namegeof} /></td>
                        <td>{poi.codegeof}</td>
                        <td>{poi.descriptiongeof}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : searchFieldPoi.trim() ? (
            <div className="col-md-12" style={{ marginTop: '5px' }}>
              <div className="overlay" style={{ display: 'none' }}></div>
              <div
                id="alarm_4"
                className='autocomplete-items'
                style={{ border: '1px solid #ccc', overflow: 'auto', backgroundColor: 'white', height: '250px', paddingLeft: '5px' }}
              >
                <p>{translate("No POI found")}</p>
              </div>
            </div>
          ) : null}
        </div>
      )}
      <div className="col-md-12" style={{ marginTop: '5px' }}>
        <div className="overlay" style={{ display: 'none' }}></div>
        <div style={{ border: '1px solid #ccc', overflow: 'auto', backgroundColor: 'white', height: '300px', paddingLeft: '5px' }}>
          <table id="poi_table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th className="checkbox" style={{ width: '1.5em' }}>
                  <input
                    type="checkbox"
                    id="selectPoi"
                    name="selectPoi"
                    checked={selectAll}
                    onChange={handleSelectAllChange}
                  />
                  <label htmlFor="selectPoi" style={{ verticalAlign: 'middle' }}></label>
                </th>
                <th>ID {translate("POI")}</th>
                <th>{translate("Name")}</th>
                <th>{translate("Code")}</th>
                <th>{translate("Description")}</th>
              </tr>
            </thead>
            <tbody>
              {pois.length > 0 ? (
                pois.map((poi) => (
                  <tr key={poi.idgeof}>
                    <td className="checkbox">
                      <input
                        type="checkbox"
                        id={`poi_${poi.idgeof}_cb`}
                        name="poi_id"
                        value={poi.idgeof}
                        data-numpts={poi.numPoints}
                        checked={isChecked(poi.idgeof.toString())}
                        onChange={() => handleCheckboxChange(poi)}

                      />
                      <label htmlFor={`poi_${poi.idgeof}_cb`}></label>
                    </td>
                    <td>{poi.idgeof}</td>
                    <td>{poi.namegeof}</td>
                    <td>{poi.codegeof}</td>
                    <td>{poi.descriptiongeof}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} style={{ textAlign: "center" }}>
                    {translate("No associated POI")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PoiAlarm;
